import { Injectable } from '@angular/core';
import {
  LocationsQuery,
  LocationsStore,
  SocketService,
} from '@tablesready/frontend-store';
import { map, merge, Observable, tap } from 'rxjs';
import { Location } from '@shared/location/models';
import { PaymentPlanCodes } from '@shared/price-plan/enums';
import { WebSocketEventsEnum } from '@shared/websocket';
import { AccountApiService } from './account-api.service';

@Injectable({ providedIn: 'root' })
export class LocationsService {
  constructor(
    private readonly accountApiService: AccountApiService,
    private readonly store: LocationsStore,
    private readonly query: LocationsQuery,
    private readonly socketService: SocketService,
  ) {}

  load(): Observable<Location[]> {
    return this.accountApiService.getLocations()
      .pipe(tap(locations => this.store.set(locations)));
  }

  reset(): void {
    this.store.reset();
  }

  selectAll(): Observable<Location[]> {
    return this.query.selectAll();
  }

  getAll(): Location[] {
    return this.query.getAll();
  }

  getById(id: string): Location {
    return this.query.getEntity(id);
  }

  countFreeLocations(): Observable<number> {
    return this.selectAll()
      .pipe(
        map(locations => locations.filter(location => location.recurly.planCode === PaymentPlanCodes.Free && location.enabled).length),
      );
  }

  getCountOfFreeLocations(): number {
    return this.getAll().filter(location => location.recurly.planCode === PaymentPlanCodes.Free && location.enabled).length
  }

  listenToChanges() {
    return merge(
      this.socketService
        .getEventStream<Location>(WebSocketEventsEnum.locationCreated)
        .pipe(tap(location => this.store.add(location))),
      this.socketService
        .getEventStream<Location>(WebSocketEventsEnum.locationUpdated)
        .pipe(tap(location => this.store.update(location._id, location))),
      this.socketService
        .getEventStream<string[]>(WebSocketEventsEnum.locationDeleted)
        .pipe(tap(ids => this.store.remove(ids))),
    );
  }
}
